/**
 * bugSubmitService — UAT-portal gated bug-report submit client.
 *
 * Story: epic-uat-portal-001-10.
 *
 * Posts a tester's bug report to the net-new, tester-session-gated oracle-bridge
 * endpoint `POST /api/uat/bugs`. The endpoint is **multipart/form-data** (it takes
 * 0..6 screenshot files alongside the seven text fields), so this builds a
 * `FormData` and lets the browser set the `multipart/boundary` Content-Type —
 * setting it by hand would omit the boundary and break the upload.
 *
 * Request goes through `authenticatedFetch`, which injects the tester bearer and
 * owns the 401 lifecycle (clear token + redirect to /login). On 401 this still
 * throws so the page never mistakes a dead session for a success.
 *
 * Errors surface as a typed `BugSubmitApiError` carrying the HTTP status and the
 * backend's machine-readable `error` code, mirroring `TesterAccountApiError` so
 * the page can branch on status/code (502 bug_capture_failed → retryable banner
 * that preserves input; 400 invalid_upload / 'Validation failed' → clear message)
 * rather than parsing strings.
 *
 * Backend contract (shipped + deployed on oracle.staging.helloworlddao.com):
 *   POST /api/uat/bugs   multipart/form-data, Authorization: Bearer <session>
 *     text: url, did, expected, actual, errorsPage, errorsConsole, errorsNetwork
 *     file: screenshots (0..6, png/jpeg/webp, <=5MB each)
 *     → 201 {id}
 *     | 400 {error:'invalid_upload'|'Validation failed'}
 *     | 401
 *     | 502 {error:'bug_capture_failed'}
 */

import { getOracleBridgeUrl } from '@/utils/oracleBridge';
import { authenticatedFetch } from '@/auth/authenticatedFetch';

/** The seven text fields of a bug report, mapped 1:1 onto the multipart body. */
export interface BugSubmitFields {
  url: string;
  did: string;
  expected: string;
  actual: string;
  errorsPage: string;
  errorsConsole: string;
  errorsNetwork: string;
}

/**
 * Typed error for `POST /api/uat/bugs` failures. `code` is the backend's
 * machine-readable `error` field ('invalid_upload' / 'Validation failed' /
 * 'bug_capture_failed') when present, else a generic fallback.
 */
export class BugSubmitApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'BugSubmitApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Read a JSON error body WITHOUT letting a parse failure pose as data. A
 * non-JSON / empty body yields a generic code, never a thrown parse error that
 * would mask the real HTTP status (failure-as-plausible-value: the status is the
 * source of truth, HTTP decided by 201/400/401/502 — never by a field in body).
 */
async function readErrorCode(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body?.error === 'string' ? body.error : 'unknown_error';
  } catch {
    return 'unknown_error';
  }
}

/**
 * Submit a bug report (seven text fields + 0..6 screenshot files) to the
 * tester-gated oracle-bridge endpoint.
 *
 * @param fields - the seven text fields (empty strings for the optional ones)
 * @param files  - 0..6 screenshot File objects (already client-side validated)
 * @returns `{ id }` — the newly filed bug's member-facing id
 * @throws BugSubmitApiError on 400 (invalid_upload / Validation failed),
 *   401 (session over — authenticatedFetch has already redirected), 502
 *   (bug_capture_failed — retryable), or a malformed 201 body.
 */
export async function submitBug(
  fields: BugSubmitFields,
  files: File[],
): Promise<{ id: string }> {
  const form = new FormData();
  form.append('url', fields.url);
  form.append('did', fields.did);
  form.append('expected', fields.expected);
  form.append('actual', fields.actual);
  form.append('errorsPage', fields.errorsPage);
  form.append('errorsConsole', fields.errorsConsole);
  form.append('errorsNetwork', fields.errorsNetwork);
  for (const file of files) {
    form.append('screenshots', file);
  }

  // Intentionally NO Content-Type header: the browser sets
  // `multipart/form-data; boundary=…` itself. Setting it by hand drops the
  // boundary and the server cannot parse the parts.
  const response = await authenticatedFetch(`${getOracleBridgeUrl()}/api/uat/bugs`, {
    method: 'POST',
    body: form,
  });

  if (!response.ok) {
    const code = await readErrorCode(response);
    let message: string;
    if (response.status === 502) {
      message = 'The server could not save your report right now. Please try again.';
    } else if (response.status === 401) {
      message = 'Your tester session has expired. Please sign in again.';
    } else if (code === 'invalid_upload') {
      message =
        'One or more screenshots were rejected. Attach up to 6 PNG, JPEG, or WebP images, 5 MB or less each.';
    } else if (response.status === 400) {
      message = 'Please check the form — some required details are missing or invalid.';
    } else {
      message = 'Could not file your report. Please try again.';
    }
    throw new BugSubmitApiError(message, response.status, code);
  }

  const body = (await response.json().catch(() => null)) as { id?: unknown } | null;
  if (!body || typeof body.id !== 'string') {
    throw new BugSubmitApiError(
      'The server accepted the report but returned an unexpected response.',
      response.status,
      'malformed_response',
    );
  }
  return { id: body.id };
}
